import React from 'react';
import Moment from 'moment';
import { Flex, Space, Typography, Button, Popconfirm, Tag, Tooltip } from 'antd';
import {
  CalendarOutlined,
  HourglassOutlined,
  AppstoreOutlined,
  SendOutlined,
  ClockCircleOutlined,
  TranslationOutlined,
} from '@ant-design/icons';
import { useStore } from '../../../inbox-store';
import ViewerAvatarGroup from '../../../../common/ViewerAvatarGroup';
import { useFeature } from '../../../../common/FeatureGate';
import { getUserLanguage, langName } from '../../../../../utils/languages';
import ChannelIcon from '../../../../common/ChannelIcon';
import { getWhatsAppChannel } from '../../../utils/whatsappChannel';

const { Text } = Typography;

// ADR-025: `whatsapp_hold` = a reply held until the candidate reopens the 24h window.
type Kind = 'followup' | 'event_reminder' | 'module_time' | 'module_no_reply' | 'whatsapp_hold';

interface PendingItem {
  id: string;
  message: string;
  created_at: string;
  kind?: Kind | null;
  trigger_id?: string | null;
  trigger_type?: string | null;
  trigger_config?: Record<string, any> | null;
  module_id?: string | null;
  module_title?: string | null;
  calendar_event_id?: string | null;
  event_start?: string | null;
  event_timezone?: string | null;
  cancel_on_reply?: boolean;
  contact_id?: string;
  conversation_id?: string | null;
  conversation?: { id?: string | null; contact_id?: string | null } | null;
  channelDetails?: { type: string; id: string | null; name: string; avatar_url: string | null } | null;
  channel_name?: string | { name?: string };
  user?: { id: string | null; name: string; avatar_url: string | null } | null;
}

const bubbleBase: React.CSSProperties = {
  textAlign: 'start',
  background: 'white',
  border: '1px dashed gray',
  padding: '8px 12px',
  borderRadius: 8,
  maxWidth: '75%',
  alignSelf: 'flex-end',
};

const chip = (label: string, icon?: React.ReactNode) => (
  <Tag
    style={{
      marginInlineEnd: 6,
      fontSize: 11,
      lineHeight: '16px',
      maxWidth: '100%',
      whiteSpace: 'normal',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
    }}
  >
    {icon}
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
  </Tag>
);

const conditionalLabel = (item: PendingItem) => {
  if (!item.created_at) return '';
  return `If no reply by ${Moment(item.created_at).calendar()}, send:`;
};

const Chips: React.FC<{ item: PendingItem }> = ({ item }) => {
  const kind: Kind = (item.kind as Kind) || 'followup';
  if (kind === 'whatsapp_hold') {
    return (
      <Tooltip title="WhatsApp only allows free-form replies for 24 hours after the candidate's last message. This goes out automatically when they reply.">
        {chip('Waiting for candidate reply · WhatsApp', <ChannelIcon channel="whatsapp" size={12} />)}
      </Tooltip>
    );
  }
  if (kind === 'event_reminder') {
    const when = item.event_start ? Moment(item.event_start).calendar() : null;
    return chip(when ? `Event reminder · ${when}` : 'Event reminder', <CalendarOutlined />);
  }
  if (kind === 'module_time') {
    return chip(item.module_title ? `Flow: ${item.module_title}` : 'Flow trigger', <AppstoreOutlined />);
  }
  if (kind === 'module_no_reply') {
    return chip(item.module_title ? `Flow: ${item.module_title}` : 'No-reply trigger', <HourglassOutlined />);
  }
  // followup
  const when = item.created_at ? Moment(item.created_at).calendar() : null;
  return (
    <Tooltip title="Sends automatically unless the candidate books a time or you cancel.">
      {chip(when ? `Follow-up · ${when}` : 'Follow-up', <ClockCircleOutlined />)}
    </Tooltip>
  );
};

const PendingMessageBubble: React.FC<{ item: PendingItem }> = ({ item }) => {
  const cancelScheduledMessage = useStore((s: any) => s.cancelScheduledMessage);
  const sendScheduledMessageNow = useStore((s: any) => s.sendScheduledMessageNow);
  const muteContactReminders = useStore((s: any) => s.muteContactEventReminders);
  const dropEnrollmentForContact = useStore((s: any) => s.dropEnrollmentForContact);
  const cancelWhatsAppHold = useStore((s: any) => s.cancelWhatsAppHold);
  // ADR-025: is this pending row on the contact's WhatsApp conversation?
  const activeContactModel = useStore((s: any) => s.activeContactModel);
  const waChannel = getWhatsAppChannel(activeContactModel);
  const onWhatsAppConversation = !!waChannel?.conversationId
    && (item.conversation_id === waChannel.conversationId || item.conversation?.id === waChannel.conversationId);
  // ADR-035: scheduled text is stored in the flow's base language and translated by
  // the scheduler at send time when the contact's language differs. The viewer's
  // language is the best client-side proxy for the base language.
  const contactLanguage = useStore((s: any) => s.activeContactModel?.language);
  const canTranslate = useFeature('ai-translation');
  const willTranslate = canTranslate && !!contactLanguage && contactLanguage !== getUserLanguage();

  const kind: Kind = (item.kind as Kind) || 'followup';

  const renderActions = () => {
    const actions: React.ReactNode[] = [];

    if (kind === 'whatsapp_hold') {
      actions.push(
        <Popconfirm
          key="cancel-hold"
          title="Cancel this held WhatsApp message?"
          description="It will not be sent when the candidate replies."
          onConfirm={() => cancelWhatsAppHold(item.id)}
          okText="Yes"
          cancelText="No"
          placement="left"
        >
          <Button type="text" size="small" data-testid="whatsapp-hold-cancel">
            <Text type="secondary" style={{ fontSize: 'x-small', fontWeight: 500 }}>
              Cancel
            </Text>
          </Button>
        </Popconfirm>,
      );
      return actions;
    }

    actions.push(
      <Popconfirm
        key="cancel"
        title="Cancel this scheduled message?"
        onConfirm={() => cancelScheduledMessage(item.id)}
        okText="Yes"
        cancelText="No"
        placement="left"
      >
        <Button type="text" size="small">
          <Text type="secondary" style={{ fontSize: 'x-small', fontWeight: 500 }}>
            Cancel
          </Text>
        </Button>
      </Popconfirm>,
    );

    if (kind === 'event_reminder' && item.contact_id) {
      actions.push(
        <Popconfirm
          key="mute"
          title="Mute reminders for this contact?"
          description="Future calendar events won't schedule reminders, and pending ones are cancelled."
          onConfirm={() => muteContactReminders(item.contact_id, true)}
          okText="Mute"
          cancelText="Back"
          placement="left"
        >
          <Tooltip title="Mute event reminders for this contact">
            <Button type="text" size="small">
              <Text type="secondary" style={{ fontSize: 'x-small', fontWeight: 500 }}>
                Mute for contact
              </Text>
            </Button>
          </Tooltip>
        </Popconfirm>,
      );
    }

    if (kind === 'module_no_reply') {
      actions.push(
        <Popconfirm
          key="send-now"
          title="Send this message now, regardless of reply status?"
          onConfirm={() => sendScheduledMessageNow(item.id)}
          okText="Send now"
          cancelText="Back"
          placement="left"
        >
          <Button type="text" size="small" icon={<SendOutlined />}>
            <Text type="secondary" style={{ fontSize: 'x-small', fontWeight: 500 }}>
              Send now
            </Text>
          </Button>
        </Popconfirm>,
      );
    }

    if (kind === 'module_time' && item.module_id) {
      actions.push(
        <Popconfirm
          key="drop-flow"
          title="Drop this contact from the flow?"
          description="All pending flow-triggered messages for this contact will also be cancelled."
          onConfirm={() => dropEnrollmentForContact(item.contact_id, item.module_id)}
          okText="Drop"
          cancelText="Back"
          placement="left"
          okButtonProps={{ danger: true }}
        >
          <Button type="text" size="small" danger>
            <Text type="secondary" style={{ fontSize: 'x-small', fontWeight: 500, color: '#ff4d4f' }}>
              Drop from flow
            </Text>
          </Button>
        </Popconfirm>,
      );
    }

    return actions;
  };

  // Mirror ThreadItem's avatar resolution so trigger/follow-up/reminder bubbles show the
  // same channel face as their eventual outbound thread.
  const channelInfo = item.channelDetails
    || (typeof item.channel_name === 'object' ? item.channel_name : null);
  const avatarViewer = channelInfo
    ? {
        name: (channelInfo as any).name || 'User',
        avatar_url: (channelInfo as any).avatar_url || '',
        type: (channelInfo as any).type || 'user',
        id: (channelInfo as any).id || '',
      }
    : null;

  return (
    <Flex vertical align="flex-end" style={{ width: '100%' }} key={item.id}>
      <div className="chat-bubble" style={bubbleBase}>
        <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
          <div style={{ marginBottom: 4, maxWidth: '100%', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Chips item={item} />
            {kind !== 'whatsapp_hold' && onWhatsAppConversation && (
              <Tooltip title="This conversation is on WhatsApp. If the 24h window is closed when this sends, it goes as an approved template, or as SMS if the contact has a mobile number.">
                <Tag
                  color="blue"
                  data-testid="pending-via-whatsapp"
                  style={{ marginInlineEnd: 6, fontSize: 11, lineHeight: '16px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <ChannelIcon channel="whatsapp" size={12} />
                  <span>Via WhatsApp · template or SMS if window closed</span>
                </Tag>
              </Tooltip>
            )}
            {willTranslate && (
              <Tooltip title={`This contact writes in ${langName(contactLanguage)}. The message is translated automatically when it is sent; you'll see your text with "Show sent text" afterwards.`}>
                {chip(`Will be sent in ${langName(contactLanguage)}`, <TranslationOutlined />)}
              </Tooltip>
            )}
          </div>
          {kind === 'module_no_reply' ? (
            <Text type="secondary" style={{ fontSize: 'smaller', fontStyle: 'italic', display: 'block', marginBottom: 4 }}>
              {conditionalLabel(item)}
            </Text>
          ) : null}
          <Text
            style={{
              color: 'gray',
              fontStyle: 'italic',
              fontSize: 'smaller',
              whiteSpace: 'pre-wrap',
            }}
          >
            {item.message}
          </Text>
        </div>
      </div>
      <Space
        size="small"
        wrap
        style={{
          marginTop: 4,
          alignItems: 'center',
          justifyContent: 'flex-end',
          rowGap: 4,
        }}
      >
        {renderActions()}
        <Text type="secondary" style={{ fontSize: 'x-small', marginLeft: 8 }}>
          {kind === 'whatsapp_hold'
            ? `Held since ${Moment(item.created_at).calendar()}`
            : `Scheduled for ${Moment(item.created_at).calendar()}`}
        </Text>
        {avatarViewer ? <ViewerAvatarGroup viewers={[avatarViewer]} /> : null}
      </Space>
    </Flex>
  );
};

export default PendingMessageBubble;
